import { di } from 'react-magnetic-di';
import { markViewMetric, REQUEST_END, REQUEST_START } from '@atlassian/jira-business-performance';
import defaultOptions from '@atlassian/jira-common-constants/src/fetch-default-options';
import { ff } from '@atlassian/jira-feature-flagging';
import { FetchError } from '@atlassian/jira-fetch';
import type { RestResponse, Workflow, WorkflowOld } from './types';

export type { Workflow, WorkflowOld, Transition, Person, Column, Issue, IssueType } from './types';

export type WorkflowIssuesData = {
	workflows: Workflow[];
	lastChangedTime: string | null;
};

/** @deprecated To be deleted when jwm.board-awesome-enhancements is cleaned up */
export type WorkflowIssuesDataOld = {
	workflows: WorkflowOld[];
	lastChangedTime: string | null;
};

export class Unchanged extends Error {}

export const fetchWorkflowIssues = async (
	projectKey: string,
	ifChangedSince?: string,
): Promise<WorkflowIssuesData> => {
	di(fetch);

	try {
		markViewMetric('jwm-board', REQUEST_START);

		const url = `/rest/board-api/1/board/${projectKey}/workflows?excludeLabels=true`;

		const response = await fetch(url, {
			...defaultOptions,
			headers: ifChangedSince ? { 'x-aif-changed-since': ifChangedSince } : undefined,
		});

		if (!response.ok) {
			if (response.status === 304) {
				throw new Unchanged('Workflow issues have not changed since the last request');
			}

			throw new FetchError(response.status);
		}

		const lastChangedTime = response.headers.get('x-alast-changed-time');

		const workflows: RestResponse = await response.json();

		let cleanedWorkflows: Workflow[];
		// As part of BAE-304 the workflows endpoint will return issueTypes for each workflow.
		// This is a breaking change for the board, so we need to make sure that we only return the
		// data that the board is expecting. The new board implementation handles the issueTypes
		if (!ff('jwm.board-awesome-enhancements')) {
			// @ts-expect-error TODO fix when BAE is cleaned up
			cleanedWorkflows = workflows.data.map((workflow) => {
				const { issueTypes, ...cleanedWorkflow } = workflow;

				return cleanedWorkflow;
			});
		} else {
			cleanedWorkflows = workflows.data;
		}

		return { workflows: cleanedWorkflows, lastChangedTime };
	} finally {
		markViewMetric('jwm-board', REQUEST_END);
	}
};
